import { endOfDay } from 'date-fns';
import { setTaskListCount, setSidebarItem } from '../slicers/sidebarSlice';
import {
    setUserGroups,
    setOrganizationsHierarchy,
    setUserOrganizations,
} from '../slicers/UserSlice';

/**
 * Fetches groups for admin or the given user and updates Redux: user groups,
 * organizations hierarchy, and deduplicated user organizations list.
 * @param {import('axios').AxiosInstance} axios - Axios instance (e.g. from useAxios(envURL))
 * @param {string|number} userId - Account ID when not admin
 * @param {boolean} isAdmin - If true, calls admin/groups; otherwise list/groups/:userId
 * @param {Function} dispatch - Redux dispatch
 */
export const refreshUserGroupsAndHierarchy = async (axios, userId, isAdmin, dispatch) => {
    const groupsApi = isAdmin ? 'admin/groups' : `list/groups/${userId}`;
    try {
        const responseData = await axios.get(groupsApi);
        if (responseData.data.data) {
            const groups = responseData.data.data;
            dispatch(setUserGroups(groups));
            dispatch(setOrganizationsHierarchy(groups));
            const tempOrgs = [
                ...new Map(
                    groups.map((org) => [
                        org.organizationName,
                        { name: org.organizationName, id: org.organizationId },
                    ])
                ).values(),
            ];
            dispatch(setUserOrganizations(tempOrgs));
        } else {
            console.error('Error fetching user groups');
        }
    } catch (err) {
        console.error('Error refreshing user groups and hierarchy:', err);
    }
};

/**
 * Fetches staff tasks for the given user and updates the sidebar task list count
 * (Pending tasks only). Use after login, on WebSocket 'staff_task' events, or
 * whenever the count should be refreshed.
 * @param {Function} axios - Axios instance (e.g. from useAxios(envURL))
 * @param {string} userId - Account ID for the staff user
 * @param {Function} dispatch - Redux dispatch
 */
export const refreshStaffTaskCount = async (axios, userId, dispatch) => {
    try {
        const staffTasksResponse = await axios.get(`staffTasks/list/${userId}`);
        if (staffTasksResponse.data.data) {
            const newCount = staffTasksResponse.data.data.filter(
                (task) => task.status === 'Pending'
            ).length;
            dispatch(setTaskListCount(newCount));
        } else {
            console.error('Error fetching Task List');
        }
    } catch (error) {
        console.error('Error refreshing staff task count:', error);
    }
};


/**
 * Fetches staff tasks for the given user and updates the sidebar task list count
 * (Pending tasks only). Use after login, on WebSocket 'staff_task' events, or
 * whenever the count should be refreshed.
 * @param {Function} axios - Axios instance (e.g. from useAxios(envURL))
 * @param {string} userId - Account ID for the staff user
 * @param {Function} dispatch - Redux dispatch
 */
export const refreshNewReportCount = async (axios, userId, dispatch) => {
    try {
        const reportResponse = await axios.get(`report/newReportCount/${userId}`);
        if (reportResponse.data.data != null) {
            dispatch(setSidebarItem({key: "newReportCount", value: reportResponse.data.data}));
        } else {
            console.error('Error fetching New Report Count');
        }
    } catch (error) {
        console.error('Error refreshing new report count:', error);
    }
};

/**
 * Fetches today's appointments for the physician and updates sidebar `appointmentsCount`.
 * @param {Function} axios - Axios instance (e.g. from useAxios(envURL))
 * @param {string|number} userId - Physician account ID
 * @param {Function} dispatch - Redux dispatch
 */
export const refreshTodaysAppointmentsCount = async (axios, userId, dispatch) => {
    try {
        const today = new Date();
        const endDate = endOfDay(today);
        const startDateTime = today.toISOString();
        const endDateTime = endDate.toISOString();
        const appointmentsResponse = await axios.get(
            `appointment?physicianIds=${encodeURIComponent(userId)}&appointmentStartDateTime=${encodeURIComponent(startDateTime)}&appointmentEndDateTime=${encodeURIComponent(endDateTime)}`
        );
        const validAppointments = appointmentsResponse.data.data.filter(appointment => !appointment.deletedAt);
        const appointmentsCount = validAppointments.length;
        dispatch(
            setSidebarItem({ key: 'appointmentsCount', value: appointmentsCount })
        );
    } catch (error) {
        console.error('Error refreshing today appointments count:', error);
    }
};
